import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ChartOptions,
} from "chart.js";
import { useSearchParams } from "react-router-dom";
import type { InrixVolumeFeature } from "../../../map/views/OrderItem/InrixVolumes/models/InrixVolumeFeature";
import { InrixVolumeDayOfWeek } from "../../../map/views/OrderItem/InrixVolumes/controls/GetOrderItemInrixVolumeParams";
import { useThemeContext } from "../../../theme/ThemeContext";
import { Theme } from "../../../../constant/Theme";
import SelectMaxButtons, { MaxType } from "./SelectMaxButtons";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

interface SegmentChartProps {
  feature: InrixVolumeFeature;
}

const generateTimeLabels = (): string[] => {
  const labels: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      labels.push(
        `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
      );
    }
  }
  return labels;
};

const dayOfWeekOrder = [
  InrixVolumeDayOfWeek.SUNDAY,
  InrixVolumeDayOfWeek.MONDAY,
  InrixVolumeDayOfWeek.TUESDAY,
  InrixVolumeDayOfWeek.WEDNESDAY,
  InrixVolumeDayOfWeek.THURSDAY,
  InrixVolumeDayOfWeek.FRIDAY,
  InrixVolumeDayOfWeek.SATURDAY,
];

const SegmentChart: React.FC<SegmentChartProps> = ({ feature }) => {
  const [maxType, setMaxType] = useState<MaxType>("all-roads");
  const { theme } = useThemeContext();
  const isDark = theme === Theme.DARK;
  const [searchParams] = useSearchParams();

  const dayOfWeekParam = searchParams.get(
    "dayOfWeek",
  ) as InrixVolumeDayOfWeek | null;
  const dayIndex = dayOfWeekParam ? dayOfWeekOrder.indexOf(dayOfWeekParam) : 0;

  const timeLabels = useMemo(() => generateTimeLabels(), []);
  const xAxisMax = useMemo(() => {
    if (maxType === "all-roads") {
      return feature.volMax;
    }

    if (feature.volumes.length === 0) {
      return feature.volMax;
    }

    return Math.max(...feature.volumes);
  }, [feature.volMax, feature.volumes, maxType]);

  const chartData = useMemo(() => {
    if (!feature.volumes || feature.volumes.length === 0) {
      return {
        labels: timeLabels,
        datasets: [],
      };
    }

    const startIndex = dayIndex * 96;
    const endIndex = startIndex + 96;
    const dayVolumes = feature.volumes.slice(startIndex, endIndex);

    const timeSegmentParam = searchParams.get("timeSegment");
    const selectedTimeSegment =
      timeSegmentParam ? Number(timeSegmentParam) : null;

    const colors = dayVolumes.map((_, index) =>
      index === selectedTimeSegment ? "#ff9800" : "#00587c",
    );

    return {
      labels: timeLabels,
      datasets: [
        {
          label: "Volume",
          data: dayVolumes,
          backgroundColor: colors,
          borderColor: colors,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [feature.volumes, dayIndex, timeLabels, searchParams]);

  const textColor = isDark ? "#ffffff" : "#000000";
  const gridColor = isDark ? "#555555" : "#e0e0e0";

  const getTimeRange = (timeIndex: number): string => {
    const hour = Math.floor(timeIndex / 4);
    const minute = (timeIndex % 4) * 15;

    let nextHour = hour;
    let nextMinute = minute + 15;
    if (nextMinute === 60) {
      nextMinute = 0;
      nextHour = (hour + 1) % 24;
    }

    const startTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    const endTime = `${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(2, "0")}`;
    return `${startTime}-${endTime}`;
  };

  const options: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? "#333333" : "#ffffff",
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1,
        callbacks: {
          title: (context) => {
            if (context.length > 0) {
              const dataIndex = context[0].dataIndex;
              return getTimeRange(dataIndex);
            }
            return "";
          },
          label: (context) => {
            return `Volume: ${context.parsed.x}`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          color: textColor,
          font: {
            size: 11,
          },
        },
        grid: {
          color: gridColor,
        },
      },
      x: {
        max: xAxisMax,
        ticks: {
          color: textColor,
        },
        grid: {
          color: gridColor,
        },
      },
    },
  };

  if (!feature.volumes || feature.volumes.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        <p>No volume data available</p>
      </div>
    );
  }

  return (
    <div
      className="d-flex flex-column flex-grow-1 w-100"
      style={{ minHeight: 0 }}
    >
      <SelectMaxButtons maxType={maxType} onSelect={setMaxType} />
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default SegmentChart;
